/**
 * Cliente del endpoint `GET /version` (público) — Sprint 16 S16-10.
 *
 * Análogo al cliente del portal pero adaptado para superadmin:
 * `sa.calbucura.cl` siempre muestra el módulo `administracion`, no hay
 * detección por pathname.
 *
 * Cache stale-while-revalidate en localStorage con TTL 5 min para
 * minimizar latencia del footer.
 */

const baseUrl = import.meta.env.VITE_API_BASE_URL;

if (!baseUrl) {
  throw new Error('VITE_API_BASE_URL es obligatoria. Revisa .env.');
}

const CACHE_KEY = 'calbucura_version_cache_sa_v1';
const TTL_MS = 5 * 60 * 1000;

export interface VersionResponse {
  portal: string | null;
  administracion: string | null;
  tesoreria: string | null;
  comision: string | null;
  consultado_at: string;
}

interface CacheEntry {
  data: VersionResponse;
  guardado_at: number;
}

function leerCache(): CacheEntry | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CacheEntry;
  } catch {
    return null;
  }
}

function guardarCache(data: VersionResponse): void {
  try {
    const entry: CacheEntry = { data, guardado_at: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    /* silencioso */
  }
}

async function fetchVersion(): Promise<VersionResponse> {
  const res = await fetch(`${baseUrl}/version`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`GET /version respondió ${res.status}`);
  }
  return (await res.json()) as VersionResponse;
}

export async function getVersion(): Promise<VersionResponse> {
  const cached = leerCache();
  const ahora = Date.now();

  if (cached && ahora - cached.guardado_at < TTL_MS) {
    return cached.data;
  }

  if (cached) {
    fetchVersion()
      .then(guardarCache)
      .catch(() => {
        /* silencioso */
      });
    return cached.data;
  }

  const fresco = await fetchVersion();
  guardarCache(fresco);
  return fresco;
}

// === Mantenedor admin /superadmin/deploys (S16-11) ===

import { apiFetch } from './client';

export type ModuloFuncional =
  | 'portal'
  | 'administracion'
  | 'tesoreria'
  | 'comision';

export interface DeployRead {
  id: string;
  modulo: ModuloFuncional;
  fecha: string; // YYYY-MM-DD
  correlativo: number;
  tag: string | null;
  sha: string;
  deployed_at: string;
  version_string: string;
}

export interface DeployFiltros {
  modulo?: ModuloFuncional;
  fecha_desde?: string;
  fecha_hasta?: string;
  limit?: number;
}

export function listarDeploys(filtros: DeployFiltros = {}): Promise<DeployRead[]> {
  const params = new URLSearchParams();
  if (filtros.modulo) params.set('modulo', filtros.modulo);
  if (filtros.fecha_desde) params.set('fecha_desde', filtros.fecha_desde);
  if (filtros.fecha_hasta) params.set('fecha_hasta', filtros.fecha_hasta);
  if (filtros.limit) params.set('limit', String(filtros.limit));
  const qs = params.toString() ? `?${params.toString()}` : '';
  return apiFetch<DeployRead[]>(`/admin/deploys${qs}`);
}
